import { useState } from 'react';
import { 
  TrendingUp, DollarSign, Calendar, Users, Home, Star,
  ArrowUpRight, ArrowDownRight, BarChart3, PieChart, Download,
  Filter, ChevronDown, Target
} from 'lucide-react';

interface MonthlyStats {
  month: string;
  revenue: number;
  expenses: number;
  bookings: number;
  occupancy: number;
  avgDailyRate: number;
}

interface PropertyPerformance {
  propertyName: string;
  revenue: number;
  bookings: number;
  occupancy: number;
  rating: number;
  reviews: number;
}

export default function RentalAnalytics() {
  const [timeRange, setTimeRange] = useState<'month' | 'quarter' | 'year'>('month');
  
  const [monthlyStats] = useState<MonthlyStats[]>([]);

  const [properties] = useState<PropertyPerformance[]>([]);

  const currentMonth = monthlyStats[monthlyStats.length - 1];
  const previousMonth = monthlyStats[monthlyStats.length - 2];
  
  const revenueChange = ((currentMonth.revenue - previousMonth.revenue) / previousMonth.revenue) * 100;
  const occupancyChange = currentMonth.occupancy - previousMonth.occupancy;
  const adrChange = ((currentMonth.avgDailyRate - previousMonth.avgDailyRate) / previousMonth.avgDailyRate) * 100;

  if (monthlyStats.length === 0) return (
    <div className="p-6"><div className="rounded-2xl border border-[var(--v-border-light)] bg-[var(--v-surface-1)] p-10 text-center">
        <p className="font-semibold text-[var(--v-text-primary)]">Sin datos todavía</p>
        <p className="mt-1 text-sm text-[var(--v-text-secondary)]">Esta sección se llenará con información real cuando registres propiedades y actividad.</p>
      </div></div>
  );

  const totalRevenue = monthlyStats.reduce((acc, m) => acc + m.revenue, 0);
  const totalExpenses = monthlyStats.reduce((acc, m) => acc + m.expenses, 0);
  const totalProfit = totalRevenue - totalExpenses;
  const profitMargin = (totalProfit / totalRevenue) * 100;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-[var(--v-text-primary)]">Análisis de Rentas</h2>
          <p className="text-slate-400">Métricas y rendimiento de propiedades</p>
        </div>
        <div className="flex gap-2">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value as any)}
            className="bg-[var(--v-surface-1)] text-white px-4 py-2 rounded-lg border border-[var(--v-surface-3)]"
          >
            <option value="month">Este Mes</option>
            <option value="quarter">Este Trimestre</option>
            <option value="year">Este Año</option>
          </select>
          <button className="bg-[var(--v-surface-1)] hover:bg-slate-700 text-white px-4 py-2 rounded-lg flex items-center gap-2">
            <Download className="w-4 h-4" />
            Exportar
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-green-600/20 to-emerald-600/20 p-5 rounded-xl border border-green-500/30">
          <p className="text-green-400 text-sm">Ingresos (Mar)</p>
          <p className="text-3xl font-bold text-white mt-1">${currentMonth.revenue.toLocaleString()}</p>
          <div className={`flex items-center gap-1 mt-2 text-sm ${revenueChange >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {revenueChange >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
            {Math.abs(revenueChange).toFixed(1)}% vs mes pasado
          </div>
        </div>
        <div className="bg-[var(--v-surface-1)] p-5 rounded-xl border border-[var(--v-surface-3)]">
          <p className="text-slate-400 text-sm">Ocupación</p>
          <p className="text-3xl font-bold text-white mt-1">{currentMonth.occupancy}%</p>
          <div className={`flex items-center gap-1 mt-2 text-sm ${occupancyChange >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {occupancyChange >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
            {Math.abs(occupancyChange)}% vs mes pasado
          </div>
        </div>
        <div className="bg-[var(--v-surface-1)] p-5 rounded-xl border border-[var(--v-surface-3)]">
          <p className="text-slate-400 text-sm">ADR (Tarifa Promedio)</p>
          <p className="text-3xl font-bold text-white mt-1">${currentMonth.avgDailyRate}</p>
          <div className={`flex items-center gap-1 mt-2 text-sm ${adrChange >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {adrChange >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
            {Math.abs(adrChange).toFixed(1)}% vs mes pasado
          </div>
        </div>
        <div className="bg-[var(--v-surface-1)] p-5 rounded-xl border border-[var(--v-surface-3)]">
          <p className="text-slate-400 text-sm">Reservas</p>
          <p className="text-3xl font-bold text-white mt-1">{currentMonth.bookings}</p>
          <p className="text-slate-500 text-sm mt-2">{(currentMonth.bookings / 30 * 100).toFixed(0)}% del mes</p>
        </div>
      </div>

      {/* Revenue Chart */}
      <div className="bg-[var(--v-surface-1)] p-5 rounded-xl border border-[var(--v-surface-3)]">
        <h3 className="font-semibold text-white mb-4">Ingresos vs Gastos</h3>
        <div className="h-48 flex items-end justify-around gap-4">
          {monthlyStats.map((stat) => (
            <div key={stat.month} className="flex-1 flex flex-col items-center gap-2">
              <div className="w-full flex gap-1 h-32">
                <div 
                  className="flex-1 bg-green-500 rounded-t"
                  style={{ height: `${(stat.revenue / 5000) * 100}%` }}
                />
                <div 
                  className="flex-1 bg-red-500 rounded-t"
                  style={{ height: `${(stat.expenses / 5000) * 100}%` }}
                />
              </div>
              <span className="text-slate-400 text-sm">{stat.month}</span>
            </div>
          ))}
        </div>
        <div className="flex justify-center gap-6 mt-4">
          <span className="flex items-center gap-2 text-sm text-slate-400">
            <div className="w-3 h-3 bg-green-500 rounded" />
            Ingresos
          </span>
          <span className="flex items-center gap-2 text-sm text-slate-400">
            <div className="w-3 h-3 bg-red-500 rounded" />
            Gastos
          </span>
        </div>
      </div>

      {/* Financial Summary */}
      <div className="bg-[var(--v-surface-1)] p-5 rounded-xl border border-[var(--v-surface-3)]">
        <h3 className="font-semibold text-white mb-4">Resumen Financiero</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-slate-700/50 rounded-lg">
            <p className="text-slate-400 text-sm">Ingresos Totales</p>
            <p className="text-2xl font-bold text-green-400">${totalRevenue.toLocaleString()}</p>
          </div>
          <div className="text-center p-4 bg-slate-700/50 rounded-lg">
            <p className="text-slate-400 text-sm">Gastos Totales</p>
            <p className="text-2xl font-bold text-red-400">${totalExpenses.toLocaleString()}</p>
          </div>
          <div className="text-center p-4 bg-slate-700/50 rounded-lg">
            <p className="text-slate-400 text-sm">Ganancia Neta</p>
            <p className="text-2xl font-bold text-blue-400">${totalProfit.toLocaleString()}</p>
          </div>
          <div className="text-center p-4 bg-slate-700/50 rounded-lg">
            <p className="text-slate-400 text-sm">Margen de Ganancia</p>
            <p className="text-2xl font-bold text-purple-400">{profitMargin.toFixed(1)}%</p>
          </div>
        </div>
      </div>

      {/* Property Performance */}
      <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-surface-3)] overflow-hidden">
        <div className="p-4 border-b border-[var(--v-surface-3)]">
          <h3 className="font-semibold text-white">Rendimiento por Propiedad</h3>
        </div>
        <div className="divide-y divide-slate-700">
          {properties.map((prop) => (
            <div key={prop.propertyName} className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="text-white font-semibold">{prop.propertyName}</h4>
                  <div className="flex items-center gap-2 mt-1">
                    <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                    <span className="text-white font-medium">{prop.rating}</span>
                    <span className="text-slate-500">({prop.reviews} reseñas)</span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-green-400">${prop.revenue.toLocaleString()}</p>
                  <p className="text-slate-500 text-sm">ingresos</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-white">{prop.bookings}</p>
                  <p className="text-slate-400 text-sm">reservas</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{prop.occupancy}%</p>
                  <p className="text-slate-400 text-sm">ocupación</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">${(prop.revenue / prop.bookings).toFixed(0)}</p>
                  <p className="text-slate-400 text-sm">promedio</p>
                </div>
              </div>
              <div className="mt-3 h-2 bg-slate-700 rounded-full overflow-hidden">
                <div 
                  className={`h-full rounded-full ${prop.occupancy > 80 ? 'bg-green-500' : prop.occupancy > 60 ? 'bg-yellow-500' : 'bg-red-500'}`}
                  style={{ width: `${prop.occupancy}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Goals */}
      <div className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 p-5 rounded-xl border border-blue-500/20">
        <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
          <Target className="w-5 h-5 text-blue-400" />
          Metas del Mes
        </h3>
        <div className="space-y-4">
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-slate-300">Ingresos: ${currentMonth.revenue.toLocaleString()} / $5,000</span>
              <span className="text-blue-400">{(currentMonth.revenue / 5000 * 100).toFixed(0)}%</span>
            </div>
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
              <div className="h-full bg-blue-500 rounded-full" style={{ width: `${Math.min(currentMonth.revenue / 5000 * 100, 100)}%` }} />
            </div>
          </div>
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-slate-300">Ocupación: {currentMonth.occupancy}% / 85%</span>
              <span className="text-blue-400">{(currentMonth.occupancy / 85 * 100).toFixed(0)}%</span>
            </div>
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
              <div className="h-full bg-purple-500 rounded-full" style={{ width: `${Math.min(currentMonth.occupancy / 85 * 100, 100)}%` }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
